import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { ShoppingCart, User, LogOut, LayoutDashboard, Menu, X } from 'lucide-react';

const Navbar = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { getCartCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24 py-3">
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link" onClick={closeMobileMenu}>
            <img
              src="/logo.png"
              alt="Wine Collection & Spirits"
              className="h-16 sm:h-20 w-auto object-contain"
              data-testid="navbar-logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-gray-900 font-medium" data-testid="nav-home">Home</Link>
            <Link to="/services" className="text-gray-700 hover:text-gray-900 font-medium" data-testid="nav-services">Services</Link>
            <Link to="/products" className="text-gray-700 hover:text-gray-900 font-medium" data-testid="nav-products">Products</Link>
            <Link to="/management" className="text-gray-700 hover:text-gray-900 font-medium" data-testid="nav-management">Management</Link>
            <Link to="/contact" className="text-gray-700 hover:text-gray-900 font-medium" data-testid="nav-contact">Contact</Link>
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                {isAdmin ? (
                  <Link
                    to="/admin"
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                    data-testid="admin-dashboard-btn"
                  >
                    <LayoutDashboard size={20} />
                    <span>Dashboard</span>
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/client/orders"
                      className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                      data-testid="my-orders-btn"
                    >
                      <User size={20} />
                      <span>My Orders</span>
                    </Link>
                    <Link
                      to="/cart"
                      className="relative flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                      data-testid="cart-btn"
                    >
                      <ShoppingCart size={20} />
                      <span>Cart</span>
                      {getCartCount() > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center" data-testid="cart-count">
                          {getCartCount()}
                        </span>
                      )}
                    </Link>
                  </>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                  data-testid="logout-btn"
                >
                  <LogOut size={20} />
                  <span>Logout</span>
                </button>
              </>
            ) : null}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center space-x-2">
            {!isAdmin && isAuthenticated && (
              <Link
                to="/cart"
                className="relative p-2"
                data-testid="mobile-cart-btn"
              >
                <ShoppingCart size={24} className="text-gray-700" />
                {getCartCount() > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {getCartCount()}
                  </span>
                )}
              </Link>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2">
              <Link 
                to="/" 
                className="px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={closeMobileMenu}
              >
                Home
              </Link>
              <Link 
                to="/services" 
                className="px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={closeMobileMenu}
              >
                Services
              </Link>
              <Link 
                to="/products" 
                className="px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={closeMobileMenu}
              >
                Products
              </Link>
              <Link 
                to="/management" 
                className="px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={closeMobileMenu}
              >
                Management
              </Link>
              <Link 
                to="/contact" 
                className="px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                onClick={closeMobileMenu}
              >
                Contact
              </Link>

              {isAuthenticated && (
                <>
                  <div className="border-t border-gray-200 my-2"></div>
                  {isAdmin ? (
                    <Link
                      to="/admin"
                      className="flex items-center space-x-2 px-4 py-3 bg-gray-900 text-white rounded-lg"
                      onClick={closeMobileMenu}
                    >
                      <LayoutDashboard size={20} />
                      <span>Dashboard</span>
                    </Link>
                  ) : (
                    <Link
                      to="/client/orders"
                      className="flex items-center space-x-2 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg"
                      onClick={closeMobileMenu}
                    >
                      <User size={20} />
                      <span>My Orders</span>
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg text-left"
                  >
                    <LogOut size={20} />
                    <span>Logout</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;